function formatIntegerWithThousandDots(digits: string): string {
    if (!digits) {
        return '';
    }
    const withoutLeadingZeros = digits.replace(/^0+(?=\d)/, '');
    const normalized = withoutLeadingZeros === '' ? '0' : withoutLeadingZeros;
    return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

function countChar(value: string, char: string): number {
    let count = 0;
    for (let i = 0; i < value.length; i++) {
        if (value[i] === char) {
            count++;
        }
    }
    return count;
}

/**
 * Trova l'indice di un punto appena inserito dall'utente confrontando con il valore precedente.
 */
function findInsertedDotIndex(raw: string, previousFormatted: string): number {
    let prevIdx = 0;
    for (let rawIdx = 0; rawIdx < raw.length; rawIdx++) {
        if (prevIdx < previousFormatted.length && raw[rawIdx] === previousFormatted[prevIdx]) {
            prevIdx++;
            continue;
        }
        if (raw[rawIdx] === '.') {
            return rawIdx;
        }
    }
    return raw.lastIndexOf('.');
}

/**
 * Normalizza il valore grezzo distinguendo i punti delle migliaia (già presenti)
 * da un eventuale punto o virgola digitato dall'utente come separatore decimale.
 */
function normalizeRawDecimalSeparator(
    raw: string,
    previousFormatted: string,
    newlyTypedDecimalIndex: number | null
): string {
    if (newlyTypedDecimalIndex !== null && newlyTypedDecimalIndex >= 0 && newlyTypedDecimalIndex < raw.length) {
        const existingCommaIndex = raw.indexOf(',');
        if (existingCommaIndex >= 0 && existingCommaIndex !== newlyTypedDecimalIndex) {
            // C'è già una virgola: rimuove il secondo separatore appena digitato
            return raw.slice(0, newlyTypedDecimalIndex) + raw.slice(newlyTypedDecimalIndex + 1);
        }
        return raw.slice(0, newlyTypedDecimalIndex) + ',' + raw.slice(newlyTypedDecimalIndex + 1);
    }

    if (raw.includes(',')) {
        return raw;
    }

    if (raw.endsWith('.')) {
        return raw.slice(0, -1) + ',';
    }

    if (previousFormatted && countChar(raw, '.') > countChar(previousFormatted, '.')) {
        const insertedDotIdx = findInsertedDotIndex(raw, previousFormatted);
        if (insertedDotIdx >= 0) {
            return raw.slice(0, insertedDotIdx) + ',' + raw.slice(insertedDotIdx + 1);
        }
    }

    return raw;
}

/**
 * Formatta il testo mentre l'utente digita: migliaia con punto, decimali con virgola (max 2).
 * Punto e virgola digitati dall'utente sono trattati come separatore decimale (virgola in output),
 * mentre i punti già presenti come separatori delle migliaia vengono ricalcolati sulle cifre intere.
 */
export function formatItalianAmountWhileTyping(
    raw: string,
    previousFormatted = '',
    newlyTypedDecimalIndex: number | null = null
): string {
    const normalized = normalizeRawDecimalSeparator(raw, previousFormatted, newlyTypedDecimalIndex);
    const sanitized = normalized.replace(/[^\d.,]/g, '');
    if (!sanitized) {
        return '';
    }

    const commaIndex = sanitized.indexOf(',');

    let integerDigits: string;
    let decimalDigits: string;

    if (commaIndex >= 0) {
        integerDigits = sanitized.slice(0, commaIndex).replace(/[.,]/g, '');
        decimalDigits = sanitized.slice(commaIndex + 1).replace(/[.,]/g, '').slice(0, 2);
    } else {
        integerDigits = sanitized.replace(/[.,]/g, '');
        decimalDigits = '';
    }

    if (!integerDigits && commaIndex < 0) {
        return '';
    }

    const formattedInteger = formatIntegerWithThousandDots(integerDigits || '0');
    if (commaIndex >= 0) {
        return `${formattedInteger},${decimalDigits}`;
    }
    return formattedInteger;
}

/**
 * Ricalcola la posizione del cursore dopo la riformattazione con i punti delle migliaia,
 * in modo che rimanga ancorato alla stessa cifra/virgola anche quando si modifica nel mezzo del testo.
 */
export function computeFormattedCaretPosition(
    raw: string,
    rawCaret: number,
    formatted: string,
    previousFormatted = '',
    newlyTypedDecimalIndex: number | null = null
): number {
    if (!formatted) {
        return 0;
    }

    const clampedCaret = Math.max(0, Math.min(rawCaret, raw.length));
    const normalized = normalizeRawDecimalSeparator(raw, previousFormatted, newlyTypedDecimalIndex);

    // Se il secondo separatore decimale è stato rimosso perché esisteva già una virgola,
    // adeguiamo l'indice del cursore sulla stringa normalizzata.
    let normCaret = clampedCaret;
    if (normalized.length < raw.length && newlyTypedDecimalIndex !== null && clampedCaret > newlyTypedDecimalIndex) {
        normCaret = clampedCaret - 1;
    }

    const commaIndexInNorm = normalized.indexOf(',');
    const commaIndexInFormatted = formatted.indexOf(',');

    if (commaIndexInNorm >= 0 && normCaret > commaIndexInNorm && commaIndexInFormatted >= 0) {
        const decimalsBeforeCaret = normalized
            .slice(commaIndexInNorm + 1, normCaret)
            .replace(/\D/g, '').length;
        return Math.min(formatted.length, commaIndexInFormatted + 1 + decimalsBeforeCaret);
    }

    const intEndInNorm = commaIndexInNorm >= 0 ? Math.min(normCaret, commaIndexInNorm) : normCaret;
    const digitsAfterCaretInInt = normalized
        .slice(intEndInNorm, commaIndexInNorm >= 0 ? commaIndexInNorm : normalized.length)
        .replace(/\D/g, '').length;

    const formattedIntEnd = commaIndexInFormatted >= 0 ? commaIndexInFormatted : formatted.length;
    if (digitsAfterCaretInInt === 0) {
        return formattedIntEnd;
    }

    let seenDigitsFromRight = 0;
    for (let i = formattedIntEnd - 1; i >= 0; i--) {
        if (/\d/.test(formatted[i])) {
            seenDigitsFromRight++;
            if (seenDigitsFromRight === digitsAfterCaretInInt) {
                return i;
            }
        }
    }

    return 0;
}

/** Completa i decimali su blur (es. "1.234" → "1.234,00"). */
export function finalizeItalianAmountOnBlur(display: string): string {
    const trimmed = display.trim();
    if (!trimmed) {
        return '';
    }

    const formatted = formatItalianAmountWhileTyping(trimmed, trimmed);
    if (!formatted) {
        return '';
    }
    if (!formatted.includes(',')) {
        return `${formatted},00`;
    }

    const [integerPart, decimalPart = ''] = formatted.split(',');
    if (decimalPart.length === 0) {
        return `${integerPart},00`;
    }
    if (decimalPart.length === 1) {
        return `${integerPart},${decimalPart}0`;
    }
    return formatted;
}

export function formatNumberToItalianAmount(value: number | null | undefined): string {
    if (value === null || value === undefined || Number.isNaN(value)) {
        return '';
    }
    const fixed = value.toFixed(2);
    const [integerPart, decimalPart] = fixed.split('.');
    return `${formatIntegerWithThousandDots(integerPart)},${decimalPart}`;
}

/** Converte la stringa visualizzata (formato IT) in numero per il salvataggio. */
export function parseItalianAmountToNumber(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined) {
        return null;
    }
    if (typeof value === 'number') {
        return Number.isNaN(value) ? null : value;
    }
    const trimmed = value.trim();
    if (!trimmed) {
        return null;
    }
    const normalized = trimmed.replace(/\./g, '').replace(',', '.');
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? null : parsed;
}
